import request from '@/api/request'

export interface CompanyInfo {
  name: string
  address: string
  phone: string
  bank: string
  bankAccount: string
  taxNo: string
  /** 公司 logo 图片 URL（单据抬头用），空为未设置 */
  logo?: string
}

export function getCompanyInfo() {
  return request.get('/settings/company')
}

export function saveCompanyInfo(data: Partial<CompanyInfo>) {
  return request.put('/settings/company', data)
}

export interface AttendanceSetting {
  officeLat: string
  officeLng: string
  officeRadius: string
  faceRequired: string
}

export function getAttendanceSettings() {
  return request.get('/settings/attendance')
}

export function saveAttendanceSettings(data: Partial<AttendanceSetting>) {
  return request.put('/settings/attendance', data)
}

export interface ContractTemplateItem {
  name: string
  intro: string
  clauses: string[]
  /** 可选完整 HTML 打印模板（配置后按此渲染；空=内置版式+intro/clauses） */
  html?: string
}

/** 可自定义打印模板的单据类型 */
export type PrintTemplateType =
  | 'quotation'
  | 'salesOrder'
  | 'salesOrderExec'
  | 'outbound'
  | 'inbound'
  | 'purchaseOrder'
  | 'return'
  | 'customerReconciliation'
  | 'supplierReconciliation'
  | 'payment'

/** 获取已自定义的打印模板与可视化配置（data: { templates, configs, hiprint }） */
export function getPrintTemplates() {
  return request.get('/settings/print-templates')
}

/** hiprint 设计器支持的单据类型（对账单流水结构暂不支持；productLabel 为商品价格标签） */
export type HiprintDocType = 'quotation' | 'salesOrder' | 'salesOrderExec' | 'outbound' | 'inbound' | 'productLabel'

/** hiprint 模板（json 为设计器模板对象） */
export interface HiprintTemplateItem {
  id: string
  name: string
  isDefault?: boolean
  json: Record<string, any>
  updatedAt?: string
}

/** 保存某单据类型的 hiprint 模板组（整组替换；空数组=清空，打印回落 Mustache 模板） */
export function saveHiprintTemplates(docType: HiprintDocType, templates: HiprintTemplateItem[]) {
  return request.put('/settings/print-hiprint', { docType, templates })
}

/** 保存单个打印模板（html 为完整模板文档） */
export function savePrintTemplate(docType: PrintTemplateType, html: string) {
  return request.put('/settings/print-templates', { docType, html })
}

/** 保存可视化模板区块配置（config 为 JSON，前端同时把编译后的 HTML 存 print-templates） */
export function savePrintTemplateConfig(docType: PrintTemplateType, config: Record<string, any>) {
  return request.put('/settings/print-template-config', { docType, config })
}

/** 删除自定义模板（恢复内置默认） */
export function deletePrintTemplate(docType: PrintTemplateType) {
  return request.delete(`/settings/print-templates/${docType}`)
}

export function getContractTemplates() {
  return request.get('/settings/contract-templates')
}

export function saveContractTemplates(templates: ContractTemplateItem[]) {
  return request.put('/settings/contract-templates', { templates })
}

export interface InventorySetting {
  locationEnabled: string
}

export function getInventorySettings() {
  return request.get('/settings/inventory')
}

export function saveInventorySettings(data: Partial<InventorySetting>) {
  return request.put('/settings/inventory', data)
}

export interface DiscountSetting {
  discountEnabled: string
}

export function getDiscountSettings() {
  return request.get('/settings/discount')
}

export function saveDiscountSettings(data: Partial<DiscountSetting>) {
  return request.put('/settings/discount', data)
}

export interface DocAddressSetting {
  addressEnabled: string
}

export function getDocAddressSettings() {
  return request.get('/settings/doc-address')
}

export function saveDocAddressSettings(data: Partial<DocAddressSetting>) {
  return request.put('/settings/doc-address', data)
}

export interface CustomerRequiredSetting {
  requirePhoneAddress: string
}

export function getCustomerRequiredSettings() {
  return request.get('/settings/customer-required')
}

export function saveCustomerRequiredSettings(data: Partial<CustomerRequiredSetting>) {
  return request.put('/settings/customer-required', data)
}

export interface DirectSalesSetting {
  directSalesEnabled: string
}

export function getDirectSalesSettings() {
  return request.get('/settings/direct-sales')
}

export function saveDirectSalesSettings(data: Partial<DirectSalesSetting>) {
  return request.put('/settings/direct-sales', data)
}

// 业务流程精简（租户级：销售/采购免审直通、出库后自动完成，存租户库 system_settings）
export interface FlowSetting {
  /** '1' 销售订单需要审批（缺省）；'0' 提交即直通 approved */
  salesApprovalRequired: string
  /** '1' 采购订单需要审批（缺省）；'0' 提交即直通 approved */
  purchaseApprovalRequired: string
  /** '1' 出库后自动完成无安装成员的订单；'0' 关闭（缺省） */
  autoCompleteOnDelivered: string
}

export function getFlowSettings() {
  return request.get('/settings/flow')
}

export function saveFlowSettings(data: Partial<FlowSetting>) {
  return request.put('/settings/flow', data)
}

export interface SalesOrderDisplaySetting {
  commissionRateVisible: string
}

export function getSalesOrderDisplaySettings() {
  return request.get('/settings/sales-order-display')
}

export function saveSalesOrderDisplaySettings(data: Partial<SalesOrderDisplaySetting>) {
  return request.put('/settings/sales-order-display', data)
}

export interface MobileDisplaySetting {
  /** 手机端库存查询成本均价显示：'1' 显示（缺省）/ '0' 隐藏 */
  stockCostPriceVisible: string
  /** 手机端首页统计数据（今日销售/今日订单数值）显示：'1' 显示 / '0' 隐藏（缺省，星号遮蔽） */
  homeStatsVisible: string
}

export function getMobileDisplaySettings() {
  return request.get('/settings/mobile-display')
}

export function saveMobileDisplaySettings(data: Partial<MobileDisplaySetting>) {
  return request.put('/settings/mobile-display', data)
}

// 介绍人类型（租户自定义，ReferrerTypeItem 定义在 api/referrer.ts）
export function getReferrerTypeSettings() {
  return request.get('/settings/referrer-types')
}

export function saveReferrerTypeSettings(types: { value: string; label: string }[]) {
  return request.put('/settings/referrer-types', { types })
}

// 手续费比例预设（租户级，比例存小数如 0.006=0.6%，最多 10 个）
export function getFeeRatePresets() {
  return request.get('/settings/fee-rate-presets')
}

export function saveFeeRatePresets(rates: number[]) {
  return request.put('/settings/fee-rate-presets', { rates })
}

export interface MapSetting {
  provider: 'nominatim' | 'tencent' | 'amap'
  tencentKey: string
  amapKey: string
  /** 腾讯 key 开启域名白名单时必填，值为白名单内的域名（如 https://example.com） */
  tencentReferer: string
}

export function getMapSettings() {
  return request.get('/settings/map')
}

export function saveMapSettings(data: Partial<MapSetting>) {
  return request.put('/settings/map', data)
}

// 提成计提方式（租户级：出库计提/按收款计提，存租户库 system_settings）
export type CommissionAccrualMode = 'outbound' | 'payment'
export type CommissionPaymentBase = 'amount' | 'actualAmount'

export interface CommissionAccrualConfig {
  accrualMode: CommissionAccrualMode
  /** 收款基数：amount=核销金额 / actualAmount=实收金额（扣手续费），仅 payment 模式有效 */
  paymentBase: CommissionPaymentBase
  /** 按收款计提生效日期（YYYY-MM-DD），null=立即生效 */
  paymentEffectiveDate: string | null
}

export function getCommissionAccrualConfig() {
  return request.get('/settings/commission-accrual')
}

export function updateCommissionAccrualConfig(data: CommissionAccrualConfig & { clearLegacyAccrued?: boolean }) {
  return request.put('/settings/commission-accrual', data)
}

// 建账向导状态（dismissed=不再显示新手引导；manualDone=手动标记完成的步骤）
export interface SetupWizardState {
  dismissed: boolean
  manualDone: Record<string, boolean>
}

export function getSetupWizard() {
  return request.get('/settings/setup-wizard')
}

export function saveSetupWizard(data: Partial<SetupWizardState>) {
  return request.put('/settings/setup-wizard', data)
}
